import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/CancelOutlined";
import SpamIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import InboxIcon from "@mui/icons-material/MoveToInboxOutlined";
import InitialsAvatar from "src/components/InitialsAvatar";
import StatusPill, { Tone } from "src/components/StatusPill";
import EmptyState from "src/components/EmptyState";
import { HTTPService } from "src/services/httpservice";
import { getHttpClient } from "src/services/rest-api";

const PAGE_SIZE = 10;
const STATUSES = ["PENDING", "APPROVED", "REJECTED", "SPAM"];

const statusTone = (status: string): Tone => {
  switch (status) {
    case "PENDING": return "warning";
    case "APPROVED": return "success";
    case "REJECTED":
    case "SPAM": return "error";
    default: return "neutral";
  }
};

const toCompanySlug = (value: string) =>
  (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const ReviewDialog = ({
  open,
  action,
  row,
  onClose,
  onSubmit,
}: {
  open: boolean;
  action: "approve" | "reject" | "spam";
  row: any;
  onClose: () => void;
  onSubmit: (payload: { message: string; company_slug?: string }) => Promise<void>;
}) => {
  const [message, setMessage] = useState("");
  const [companySlug, setCompanySlug] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setFieldError("");
    setCompanySlug(action === "approve" ? toCompanySlug(row?.company_slug || row?.company_name || "") : "");
  }, [action, open, row]);

  const title = action === "approve" ? "Approve Demo Request" : action === "reject" ? "Reject Demo Request" : "Mark as Spam";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography fontSize="13px" color="text.secondary" mb={2}>
          {row?.company_name || "This request"} from {row?.email || "the requester"}
        </Typography>
        {(row?.preferred_contact_time || row?.message) && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: "10px", bgcolor: "#F8FAFC", border: "1px solid #E2E8F0" }}>
            {row?.preferred_contact_time && (
              <Typography fontSize="12px" color="text.secondary" mb={0.75}>
                <strong>Preferred contact:</strong> {row.preferred_contact_time}
              </Typography>
            )}
            {row?.message && (
              <Typography fontSize="12px" color="text.secondary">
                <strong>Requester note:</strong> {row.message}
              </Typography>
            )}
          </Box>
        )}
        {action === "approve" && (
          <TextField
            fullWidth
            required
            label="Company Slug"
            value={companySlug}
            onChange={(e) => {
              setCompanySlug(toCompanySlug(e.target.value));
              setFieldError("");
            }}
            error={Boolean(fieldError)}
            helperText={fieldError || "Used for workspace IDs and admin/store usernames."}
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          fullWidth
          multiline
          minRows={4}
          label={action === "approve" ? "Message to include in approval mail" : action === "reject" ? "Reason to send by mail" : "Internal spam note"}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        {action === "approve" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Approval creates the company, admin account, and store accounts, then emails credentials to the requester.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color={action === "approve" ? "success" : "error"}
          disabled={loading}
          onClick={async () => {
            if (action === "approve" && companySlug.length < 3) {
              setFieldError("Enter at least 3 characters.");
              return;
            }
            setLoading(true);
            await onSubmit({
              message,
              company_slug: action === "approve" ? companySlug : undefined,
            });
            setLoading(false);
          }}
        >
          {loading ? "Saving..." : title}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FreeTrialRequestsPage = () => {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState("PENDING");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<"approve" | "reject" | "spam" | null>(null);
  const [selected, setSelected] = useState<any>(null);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getHttpClient(HTTPService.FREE_TRIAL_REQUESTS, "get", null, {
        status: status || undefined,
        page,
        limit: pageSize,
      });
      setData(res.data.data || []);
      setCount(res.data.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || "Failed to load free demo requests.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAction = (row: any, nextAction: "approve" | "reject" | "spam") => {
    setSelected(row);
    setAction(nextAction);
  };

  const submitAction = async (payload: { message: string; company_slug?: string }) => {
    if (!selected || !action) return;
    try {
      await getHttpClient(`${HTTPService.FREE_TRIAL_REQUESTS}/${selected.id}/${action}`, "post", payload);
      setAction(null);
      setSelected(null);
      fetchData();
    } catch (e: any) {
      setError(e?.message || "Could not update request.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: "1400px", mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Box>
          <Typography fontSize={24} fontWeight={800} letterSpacing={-0.3}>Free Demo Requests</Typography>
          <Typography fontSize={14} color="text.secondary" mt={0.5}>
            Review public free trial requests before creating companies and store accounts.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => (
              <Box component="span" sx={{ display: "block", lineHeight: "24px" }}>
                {value ? String(value) : "All Statuses"}
              </Box>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUSES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
        </TextField>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #E7ECF3", boxShadow: "none" }}>
        <Box px={2.5} py={2}>
          <Typography fontWeight={700} fontSize={15}>Requests</Typography>
          <Typography fontSize={13} color="text.secondary">Pending requests require admin approval before company creation.</Typography>
        </Box>
        <Divider />
        <Box px={1.5}>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 980, "& th": { bgcolor: "#FBFCFE", fontSize: "11px", fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", py: 1.25 }, "& td": { borderBottom: "1px solid #F2F5F9", py: 1.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Requester</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Outlets</TableCell>
                <TableCell>Context</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState icon={<InboxIcon />} title="No free demo requests" description="New public requests will appear here for approval." />
                  </TableCell>
                </TableRow>
              ) : data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <InitialsAvatar name={row.admin_name} size={36} />
                      <Box minWidth={0}>
                        <Typography fontSize="14px" fontWeight={700} noWrap>{row.admin_name}</Typography>
                        <Typography fontSize="12px" color="text.secondary" noWrap>{row.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px" fontWeight={700}>{row.company_name}</Typography>
                    <Typography fontSize="12px" color="text.secondary">{row.city || "—"} · {row.company_country}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px" fontWeight={700}>{row.no_of_branches}</Typography>
                    <Typography fontSize="12px" color="text.secondary">{row.restaurant_type}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography fontSize="12px" color="text.secondary" noWrap>
                      {row.preferred_contact_time || "No preferred time"}
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary" noWrap>
                      {row.message || "No note"}
                    </Typography>
                  </TableCell>
                  <TableCell><StatusPill label={row.status} tone={statusTone(row.status)} /></TableCell>
                  <TableCell><Typography fontSize="13px" color="text.secondary">{new Date(row.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</Typography></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Approve">
                        <span>
                          <IconButton size="small" disabled={row.status !== "PENDING"} onClick={() => openAction(row, "approve")} sx={{ color: "success.main" }}>
                            <CheckIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <span>
                          <IconButton size="small" disabled={row.status !== "PENDING"} onClick={() => openAction(row, "reject")} sx={{ color: "error.main" }}>
                            <CloseIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mark as spam">
                        <span>
                          <IconButton size="small" disabled={row.status === "APPROVED" || row.status === "SPAM"} onClick={() => openAction(row, "spam")} sx={{ color: "text.secondary" }}>
                            <SpamIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          <Box
            display="flex"
            justifyContent="flex-end"
            px={1.5}
            py={0.5}
            borderTop="1px solid #EEF2F6"
            sx={{
              "& .MuiTablePagination-toolbar": {
                px: { xs: 0, sm: 1 },
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
                rowGap: 0.5,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: 12,
                color: "text.secondary",
              },
            }}
          >
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={count}
              rowsPerPage={pageSize}
              page={page - 1}
              onPageChange={(_, nextPage) => setPage(nextPage + 1)}
              onRowsPerPageChange={(e) => {
                setPage(1);
                setPageSize(parseInt(e.target.value, 10));
              }}
            />
          </Box>
        </Box>
      </Card>

      <ReviewDialog
        open={Boolean(action)}
        action={action || "approve"}
        row={selected}
        onClose={() => {
          setAction(null);
          setSelected(null);
        }}
        onSubmit={submitAction}
      />
    </Box>
  );
};

export default FreeTrialRequestsPage;
